import { useEffect, useMemo, useState } from "react";
import PlaylistData from "../playlist/data/PlaylistData";
import "./SongsheetCarousel.css";
import { PlaylistItemData } from "../playlist/data/PlaylistItemData";
import PlaylistBanner from "./PlaylistBanner";
import useToggleValueWithLocalStoragePersistence from "../common/hooks/useToggleValueWithLocalStoragePersistence";
import Auth from "../auth/Auth";
import { Permission } from "../auth/Permission";
import CarouselSettingsSidebar from "./CarouselSettingsSidebar";
import CarouselPlaylistSidebar from "./CarouselPlaylistSidebar";
import { CarouselToolbar } from "./CarouselToolbar";
import { BeamerPanels } from "./BeamerPanels";
import { SlideNavigationButtons } from "./SlideNavigationButtons";
import { BeamerBroadcastChannelSender } from "../beamer/BeamerBroadcastChannelSender";
import { PlaylistRoutePaths } from "../playlist/PlaylistRoutePaths";
import { LyricsSheet } from "../beamer/LyricsSheet";
import { useStateWithLocalStoragePersistenceAndDefault } from "../common/hooks/useStateWithLocalStoragePersistenceAndDefault";
import { CarouselSlide, buildCarouselSlides } from "./slide/CarouselSlide";
import { InvalidSlide } from "./slide/InvalidSlide";
import { TextOnlySongSlide } from "./slide/TextOnlySongSlide";
import { SongsheetSlide } from "./slide/SongsheetSlide";
import SwitchUtils from "../common/SwitchUtils";
import { useHotkeys } from "react-hotkeys-hook";
import { LocalStorageKey } from "../localStoragePersistence/common/LocalStorageKey";
import { Client } from "urql";
import { useCrewLiveContext } from "../live/CrewLiveContext";
import Sidebar from "./Sidebar";
import { PlaylistLivePositionSidebarBody } from "./sidebar/liveposition/PlaylistLivePositionSidebarBody";
import { SharePlaylistSidebarBody } from "./sidebar/share/SharePlaylistSidebarBody";
import { LookupSongFunction } from "../common/LookupSongFunction";
import { useTranslation } from "react-i18next";
import { useCachedLyricsParts } from "../chordsheet/useCachedLyricsParts";
import { useDeviceContext } from "../device/DeviceInfoContext";
import { usePlaylistLivePositionSync } from "../live/playlist/usePlaylistLivePositionSync";
import { useCarouselPosition } from "./useCarouselPosition";
import { useSwipe } from "./useSwipe";
import { usePinchZoomed } from "./usePinchZoomed";
import { CarouselSidebar } from "./CarouselSidebar";
import { useCarouselSidebar } from "./useCarouselSidebar";

interface Props {
  playlistIdentifier: string;
  playlistData: PlaylistData;
  setTitle: (title: string) => void;
  setItems: (
    value:
      | ((prevState: PlaylistItemData[]) => PlaylistItemData[])
      | PlaylistItemData[],
  ) => void;
  selectedItem: PlaylistItemData | undefined;
  setSelectedItem: (item: PlaylistItemData | undefined) => void;
  auth: Auth;
  bucket: string | undefined;
  graphQlClient: Client;
  lookupSong: LookupSongFunction;
}

const channel = new BeamerBroadcastChannelSender();

export function SongsheetCarousel({
  playlistData,
  setTitle,
  setItems,
  bucket,
  selectedItem,
  setSelectedItem,
  graphQlClient,
  playlistIdentifier,
  auth,
  lookupSong,
}: Props) {
  const { t } = useTranslation();
  const {
    deviceIdentifier,
    deviceReadableName,
    deviceColor,
    setDeviceReadableName,
    setDeviceColor,
  } = useDeviceContext();

  const { currentCrewIdentifier } = useCrewLiveContext();

  const {
    openSidebar,
    open: showSidebar,
    close: closeSidebar,
  } = useCarouselSidebar();

  //an empty playlist has nothing to show, so opening it shows the playlist sidebar to add items,
  //replacing the history entry: back shall leave the empty carousel
  useEffect(() => {
    if (playlistData.getItems().length === 0)
      showSidebar("playlist", { replace: true });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const [showBanner, setShowBanner] = useToggleValueWithLocalStoragePersistence(
    new LocalStorageKey("SongsheetCarousel", "showBanner"),
    true,
  );
  const [isBeamerModeActiveSetting, setIsBeamerModeActiveSetting] =
    useToggleValueWithLocalStoragePersistence(
      new LocalStorageKey("SongsheetCarousel", "isBeamerModeActive"),
      false,
    );
  const [
    amountOfPagesInSongsheetCarousel,
    setAmountOfPagesInSongsheetCarousel,
  ] = useStateWithLocalStoragePersistenceAndDefault<number>(
    new LocalStorageKey(
      "SongsheetCarousel",
      "amountOfPagesInSongsheetCarousel",
    ),
    1,
  );
  const [listOfFollowedDevices, setListOfFollowedDevices] = useState<string[]>(
    [],
  );

  const canShowSongsheets = auth.hasPermissions([Permission.songsheetsRead]);
  const isBeamerModeActive = !canShowSongsheets || isBeamerModeActiveSetting;

  const slides = useMemo(
    () =>
      buildCarouselSlides(
        playlistData.getItems(),
        isBeamerModeActive,
        lookupSong,
      ),
    [playlistData, isBeamerModeActive, lookupSong],
  );

  const position = useCarouselPosition(slides, selectedItem, setSelectedItem);
  const {
    activeSlideIndex,
    activeItem,
    activeLyricsPart,
    setActiveLyricsPart,
    goToItem,
    goToSlide,
  } = position;

  const positionsInPlaylist = usePlaylistLivePositionSync({
    graphQlClient,
    playlistIdentifier,
    playlistData,
    isBeamerModeActive,
    listOfFollowedDevices,
    position,
  });

  const lyricsPartsOfActiveItem = useCachedLyricsParts(
    activeItem?.songEntryId || "",
  );

  function previousPageInCarousel() {
    goToSlide(activeSlideIndex - 1);
  }

  function nextPageInCarousel() {
    goToSlide(activeSlideIndex + 1);
  }

  const isPinchZoomed = usePinchZoomed();
  const swipeHandlers = useSwipe({
    onSwipeLeft: nextPageInCarousel,
    onSwipeRight: previousPageInCarousel,
  });

  useHotkeys(
    "escape",
    () => (openSidebar ? closeSidebar() : showSidebar("playlist")),
    [openSidebar],
  );
  useHotkeys("left", previousPageInCarousel, [previousPageInCarousel]);
  useHotkeys("right", nextPageInCarousel, [nextPageInCarousel]);
  //page turner pedals usually send PageUp and PageDown, which would scroll the page otherwise
  useHotkeys("pageup", previousPageInCarousel, { preventDefault: true }, [
    previousPageInCarousel,
  ]);
  useHotkeys("pagedown", nextPageInCarousel, { preventDefault: true }, [
    nextPageInCarousel,
  ]);
  function previousLyricsPart() {
    if (lyricsPartsOfActiveItem.length === 0) return;
    //leaving the first part empties the screen
    setActiveLyricsPart((current) => (current ? current - 1 : undefined));
  }

  function nextLyricsPart() {
    if (lyricsPartsOfActiveItem.length === 0) return;
    setActiveLyricsPart((current) =>
      current === undefined
        ? 0
        : Math.min(current + 1, lyricsPartsOfActiveItem.length - 1),
    );
  }

  useHotkeys("up", previousLyricsPart, [lyricsPartsOfActiveItem.length]);
  useHotkeys("down", nextLyricsPart, [lyricsPartsOfActiveItem.length]);

  function toggleSidebar(sidebar: CarouselSidebar) {
    if (openSidebar === sidebar) closeSidebar();
    else showSidebar(sidebar);
  }

  //Emptying the screen shall be sent to the beamer window
  useEffect(() => {
    if (activeLyricsPart === undefined) channel.emptyScreen();
  }, [activeLyricsPart]);

  const positionsOnActiveItem = useMemo(
    () =>
      positionsInPlaylist.filter(
        (p) => p.selectedPlaylistItemInMemoryId === activeItem?.id,
      ),
    [positionsInPlaylist, activeItem?.id],
  );

  function renderSlideContent(slide: CarouselSlide) {
    switch (slide.kind) {
      case "invalid":
        return <InvalidSlide />;
      case "textOnly":
        return <TextOnlySongSlide />;
      case "songsheet":
        return (
          <div className="carousel-slide-image-wrapper">
            <SongsheetSlide src={slide.src} />
          </div>
        );
      case "lyrics":
        return (
          <LyricsSheet
            song={slide.song}
            channel={channel}
            activeLyricsPart={activeLyricsPart}
            setActiveLyricsPart={setActiveLyricsPart}
            playlistLivePositionArray={positionsOnActiveItem}
          />
        );
      default:
        return SwitchUtils.throwUnsupportedValue(slide);
    }
  }

  function generateCarouselSlides() {
    const amountOfVisibleSlides = isBeamerModeActive
      ? 1
      : amountOfPagesInSongsheetCarousel;
    const maxStartIndex = Math.max(0, slides.length - amountOfVisibleSlides);
    const startIndex = Math.min(activeSlideIndex, maxStartIndex);
    const visibleSlides = slides.slice(
      startIndex,
      startIndex + amountOfVisibleSlides,
    );

    return (
      <div
        className={
          "carousel-slides" + (isPinchZoomed ? " carousel-pinch-zoomed" : "")
        }
        {...swipeHandlers}
      >
        {visibleSlides.map((slide) => (
          <div key={slide.key} className="carousel-slide">
            {renderSlideContent(slide)}
          </div>
        ))}
        {/* in beamer mode the beamer pad navigates, and chevrons would cover the lyrics */}
        {!isBeamerModeActive && (
          <SlideNavigationButtons
            hasPrevious={activeSlideIndex > 0}
            hasNext={activeSlideIndex < slides.length - 1}
            previous={previousPageInCarousel}
            next={nextPageInCarousel}
          />
        )}
      </div>
    );
  }

  function openBeamerWindow() {
    window.open(
      PlaylistRoutePaths.toBeamerView(playlistIdentifier),
      "beamer",
      "fullscreen=yes",
    );
  }

  return (
    /**
     * idea of the wrapper (see layout.png): http://jsfiddle.net/ch7n6/867/
     */
    <div className={"wrapper" + (openSidebar ? " carousel-sidebar-open" : "")}>
      {/* which parts are visible next to an open sidebar is decided in SongsheetCarousel.css */}
      <div className="toolbar">
        <CarouselToolbar
          playlistIdentifier={playlistIdentifier}
          openSidebar={openSidebar}
          toggleSidebar={toggleSidebar}
          canShowSongsheets={canShowSongsheets}
          isBeamerModeActive={isBeamerModeActive}
          setIsBeamerModeActive={setIsBeamerModeActiveSetting}
        />
      </div>
      {openSidebar === "settings" && (
        <Sidebar closeSidebar={closeSidebar} title={t("common.settings")}>
          <CarouselSettingsSidebar
            setShowBanner={setShowBanner}
            showBanner={showBanner}
            isBeamerModeActive={isBeamerModeActive}
            openBeamerWindow={openBeamerWindow}
            amountOfPagesInSongsheetCarousel={amountOfPagesInSongsheetCarousel}
            setAmountOfPagesInSongsheetCarousel={
              setAmountOfPagesInSongsheetCarousel
            }
          />
        </Sidebar>
      )}
      {openSidebar === "playlist" && (
        <CarouselPlaylistSidebar
          playlistData={playlistData}
          setTitle={setTitle}
          setItems={setItems}
          selectedItem={activeItem}
          setSelectedItem={goToItem}
          closeSidebar={closeSidebar}
          lookupSong={lookupSong}
          bucket={bucket}
        />
      )}
      {openSidebar === "share" && (
        <Sidebar
          closeSidebar={closeSidebar}
          title={t("carousel.toolbar.share")}
        >
          <SharePlaylistSidebarBody
            playlistIdentifier={playlistIdentifier}
            playlistData={playlistData}
            lookupSong={lookupSong}
          />
        </Sidebar>
      )}
      {openSidebar === "livePosition" && (
        <Sidebar
          closeSidebar={closeSidebar}
          title={t("carousel.livePosition.title")}
        >
          <PlaylistLivePositionSidebarBody
            deviceInfo={{ deviceIdentifier, deviceReadableName, deviceColor }}
            setDeviceReadableName={setDeviceReadableName}
            setDeviceColor={setDeviceColor}
            devicesInPlaylist={positionsInPlaylist}
            listOfFollowedDevices={listOfFollowedDevices}
            setListOfFollowedDevices={setListOfFollowedDevices}
            currentCrewIdentifier={currentCrewIdentifier}
          />
        </Sidebar>
      )}
      <div className="carousel-main">
        {showBanner && (
          <PlaylistBanner
            playlistData={playlistData}
            selectedItem={activeItem}
            setSelectedItem={goToItem}
            lookupSong={lookupSong}
          />
        )}
        <div className="carousel-stage">
          {/* before the slides, so the preview listens before the active lyrics part is sent */}
          {isBeamerModeActive && (
            <BeamerPanels
              previousSong={previousPageInCarousel}
              nextSong={nextPageInCarousel}
              previousLyricsPart={previousLyricsPart}
              nextLyricsPart={nextLyricsPart}
              emptyLyricsScreen={() => setActiveLyricsPart(undefined)}
              increaseFontSize={() => channel.increaseFontSize()}
              decreaseFontSize={() => channel.decreaseFontSize()}
              openBeamerWindow={openBeamerWindow}
            />
          )}
          {generateCarouselSlides()}
        </div>
      </div>
    </div>
  );
}
